import { AppCheckbox } from '@/components/common/AppCheckbox';
import { AppSelect } from '@/components/common/AppSelect';
import { AppTextarea } from '@/components/common/AppTextarea';
import { AppTextInput } from '@/components/common/AppTextInput';
import { ButtonGroup } from '@/components/common/ButtonGroup';
import { RadioCardGroup } from '@/components/common/RadioCard';
import { TabButton } from '@/components/common/TabButton';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import type { AppFormFieldProps } from './types';
import {
  getErrorMessage,
  getIn,
  isButtonGroupProps,
  isCheckboxProps,
  isRadioCardProps,
  isSelectProps,
  isTabsProps,
  isTextareaProps,
  omitKeys,
} from './utils';

const AppFormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: AppFormFieldProps<TFieldValues, TName>
) => {
  const { name } = props;
  const { control, formState } = useFormContext<TFieldValues>();
  const error = getIn(formState.errors, name);
  const errorMessage = getErrorMessage(error);

  if (isTabsProps(props)) {
    const tabsProps = omitKeys(props, ['fieldType']);
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <TabButton
            {...tabsProps}
            value={field.value ?? ''}
            onChange={field.onChange}
          />
        )}
      />
    );
  }

  if (isCheckboxProps(props)) {
    const checkboxProps = omitKeys(props, ['fieldType']);
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <AppCheckbox
            {...checkboxProps}
            checked={Boolean(field.value)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.checked)
            }
            onBlur={field.onBlur}
            ref={field.ref}
            error={errorMessage}
          />
        )}
      />
    );
  }

  if (isSelectProps(props)) {
    const selectProps = omitKeys(props, ['fieldType']);
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <AppSelect
            {...selectProps}
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            error={errorMessage}
          />
        )}
      />
    );
  }

  if (isTextareaProps(props)) {
    const textareaProps = omitKeys(props, ['fieldType']);
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <AppTextarea
            {...textareaProps}
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            error={errorMessage}
          />
        )}
      />
    );
  }

  if (isRadioCardProps(props)) {
    const radioCardProps = omitKeys(props, ['fieldType']);
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <RadioCardGroup
            {...radioCardProps}
            value={field.value ?? ''}
            onChange={field.onChange}
          />
        )}
      />
    );
  }

  if (isButtonGroupProps(props)) {
    const buttonGroupProps = omitKeys(props, ['fieldType']);
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <ButtonGroup
            {...buttonGroupProps}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
    );
  }

  const textInputProps = omitKeys(props, ['fieldType']);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <AppTextInput
          {...textInputProps}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          ref={field.ref}
          error={errorMessage}
        />
      )}
    />
  );
};

AppFormField.displayName = 'AppFormField';

export default AppFormField;
